import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from './Sidebar.js';
import { Topbar } from './Topbar.js';
import { BottomNav } from './BottomNav.js';
import { ModuleSubNav } from './ModuleSubNav.js';
import { modules } from '../../modules/registry.js';
import { apiFetch } from '../shared/hooks/useApi.js';

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement>(null);

  // Check onboarding status — redirect to wizard if not complete
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  const { data: onboardingStatus } = useQuery({
    queryKey: ['onboarding', 'status'],
    queryFn: () => apiFetch<{ voltooid: boolean; huidigeStap: string }>('/api/onboarding/status'),
    retry: false,
    staleTime: 60_000,
    enabled: !isOnboardingRoute,
  });

  useEffect(() => {
    if (onboardingStatus && !onboardingStatus.voltooid && !isOnboardingRoute) {
      navigate('/onboarding/setup', { replace: true });
    }
  }, [onboardingStatus, isOnboardingRoute, navigate]);

  // Scroll to top on route change
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  // Find active module (for sub-nav)
  const activeModule = modules.find(m => location.pathname.startsWith(m.basePath));
  const showSubNav = activeModule && activeModule.navItems.length > 1;

  // Full-bleed routes get no padding (they manage their own layout)
  const isFullBleed = location.pathname.startsWith('/communicatie');

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-surface">
      <Topbar />
      <div className="flex-1 flex overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0">
          {/* Sub-navigation bar for modules with multiple pages */}
          {showSubNav && <ModuleSubNav items={activeModule.navItems} />}

          {/* Main content */}
          <main
            ref={mainRef}
            className={clsx(
              'flex-1 min-w-0',
              isFullBleed ? 'overflow-hidden' : 'overflow-y-auto p-4 md:p-6',
              // Bottom padding on mobile for BottomNav
              !isFullBleed && 'pb-[76px] md:pb-6'
            )}
          >
            <Outlet />
          </main>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
